/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RecordNotFound } from "@/components/shared/record-not-found";
import {
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  PaperclipIcon,
} from "lucide-react";
import { format } from "date-fns";
import { Fragment } from "react/jsx-runtime";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { EmailInboxAttachment } from "./email-inbox-attachment";
import { ReactTableSkelton } from "../utils";

const formatDate = (value?: string) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd-MM-yyyy hh:mm a");
  } catch {
    return value;
  }
};

const isHtml = (value?: string) => !!value && /<\/?[a-z][\s\S]*>/i.test(value);

const statusClass: Record<string, string> = {
  processed: "text-green-600 bg-green-50 border-green-100",
  success: "text-green-600 bg-green-50 border-green-100",
  queued: "text-green-600 bg-green-50 border-green-100",
  failed: "text-red-600 bg-red-50 border-red-100",
  error: "text-red-600 bg-red-50 border-red-100",
};

type EmailInboxDetailsProps = {
  emailInbox: any;
  loading: boolean;
  contentClassName?: string;
};

export const EmailInboxDetails = ({
  emailInbox,
  loading,
  contentClassName,
}: EmailInboxDetailsProps) => {
  const { toast } = useToast();
  const [bodyOpen, setBodyOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const onCopyBody = async () => {
    if (!emailInbox?.body_content) return;
    try {
      await navigator.clipboard.writeText(emailInbox.body_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Unable to copy body content",
      });
    }
  };

  if (loading) return <ReactTableSkelton />;

  if (!emailInbox) {
    return (
      <Card>
        <RecordNotFound />
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardContent
        className={cn(
          "max-w-[100%] p-0 overflow-auto scrollbar h-auto md:h-[calc(100dvh-190px)]",
          contentClassName,
        )}
      >
        <div className="flex flex-col gap-5 p-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <h2 className="text-xl font-semibold break-words">
              {emailInbox.subject || "(No subject)"}
            </h2>
            <div className="flex shrink-0 items-center gap-2">
              <Badge
                variant={"outline"}
                className={`capitalize ${
                  statusClass[emailInbox.status] || ""
                }`}
              >
                {emailInbox.status}
              </Badge>
              <Badge variant={"outline"} className="capitalize">
                {emailInbox.provider}
              </Badge>
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-4 text-sm">
            <div className="flex flex-wrap gap-x-2">
              <span className="w-20 shrink-0 text-muted-foreground">From</span>
              <span className="font-medium">{emailInbox.sender || "-"}</span>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <span className="w-20 shrink-0 text-muted-foreground">To</span>
              <span className="font-medium break-words">
                {Array.isArray(emailInbox.recipient_list)
                  ? emailInbox.recipient_list.join(", ")
                  : emailInbox.recipient_list || "-"}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <span className="w-20 shrink-0 text-muted-foreground">
                Received
              </span>
              <span className="font-medium">
                {formatDate(emailInbox.received_at)}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <span className="w-20 shrink-0 text-muted-foreground">
                Folder
              </span>
              <span className="font-medium">{emailInbox.folder || "-"}</span>
            </div>
          </div>

          {emailInbox.error ? (
            <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-600">
              {emailInbox.error}
            </div>
          ) : null}

          <Separator />

          <Collapsible open={bodyOpen} onOpenChange={setBodyOpen}>
            <div className="flex items-center justify-between gap-2">
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium">
                <ChevronDownIcon
                  size={16}
                  className={`transition-transform ${
                    bodyOpen ? "rotate-0" : "-rotate-90"
                  }`}
                />
                Body
              </CollapsibleTrigger>
              <Button
                variant={"outline"}
                size={"icon"}
                onClick={onCopyBody}
                disabled={!emailInbox.body_content}
                title="Copy body"
              >
                {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
              </Button>
            </div>
            <CollapsibleContent>
              {isHtml(emailInbox.body_content) ? (
                <iframe
                  title="Email body"
                  srcDoc={`<base target="_blank">${emailInbox.body_content}`}
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  className="mt-3 w-full h-[400px] rounded-md border bg-white"
                />
              ) : (
                <div className="mt-3 break-words text-sm leading-relaxed max-h-[300px] min-h-[100px] overflow-y-auto">
                  {emailInbox.body_content ? (
                    emailInbox.body_content
                      .split("\n")
                      .map((line: string, index: number) => (
                        <Fragment key={index}>
                          {index > 0 ? <br /> : null}
                          {line}
                        </Fragment>
                      ))
                  ) : (
                    <span className="text-muted-foreground">
                      No body content
                    </span>
                  )}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {Array.isArray(emailInbox.attachment_list) &&
          emailInbox.attachment_list.length > 0 ? (
            <>
              <Separator />
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <PaperclipIcon size={16} />
                  <span>
                    {emailInbox.attachment_list.length} Attachment
                    {emailInbox.attachment_list.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {emailInbox.attachment_list.map(
                    (attachment: any, index: number) => (
                      <EmailInboxAttachment
                        key={attachment.path || index}
                        attachment={attachment}
                      />
                    ),
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
